import { ACTIVITY_HISTORY_STORE, openDeviceDatabase } from "../../lib/device-storage";
import { activityGeoJson } from "../../lib/navigation-core";
import type { NavigationSession } from "../../lib/navigation-session-store";
import type { Coordenada, Salida } from "./model";

export type BookmarkSyncStatus = "pending" | "synced";

export type Bookmark = {
  key: string;
  ownerId: string;
  activityId: string;
  saved: boolean;
  updatedAt: number;
  syncStatus: BookmarkSyncStatus;
  serverVersion: number | null;
};

export type RemoteBookmarkState = {
  activityId: string;
  saved: boolean;
  serverVersion: number;
  updatedAt: number;
};

const DB_NAME = "thera-mi-mapa";
const DB_VERSION = 2;
const STORE = "bookmarks";
const SUMMARIES = "summaries";
const PREVIEWS = "previews";

function dbBookmarks(): Promise<IDBDatabase> {
  if (!("indexedDB" in globalThis)) return Promise.reject(new Error("Almacenamiento local no disponible."));
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        const store = request.result.createObjectStore(STORE, { keyPath: "key" });
        store.createIndex("ownerId", "ownerId", { unique: false });
      }
      for (const name of [SUMMARIES, PREVIEWS]) {
        if (!request.result.objectStoreNames.contains(name)) {
          const store = request.result.createObjectStore(name, { keyPath: "key" });
          store.createIndex("ownerId", "ownerId", { unique: false });
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("Cierra otras pestanas de THERA y vuelve a intentar."));
  });
}

function normalizeBookmark(raw: Partial<Bookmark> & { key: string; ownerId: string; activityId: string; saved: boolean; updatedAt: number }): Bookmark {
  return {
    key: raw.key,
    ownerId: raw.ownerId,
    activityId: raw.activityId,
    saved: Boolean(raw.saved),
    updatedAt: Number.isFinite(raw.updatedAt) ? raw.updatedAt : 0,
    syncStatus: raw.syncStatus === "synced" ? "synced" : "pending",
    serverVersion: Number.isFinite(raw.serverVersion) ? Number(raw.serverVersion) : null,
  };
}

export async function listCachedRemoteSummaries(ownerId: string): Promise<Salida[]> {
  const db = await dbBookmarks();
  try {
    return await new Promise<Salida[]>((resolve, reject) => {
      const tx = db.transaction(SUMMARIES, "readonly");
      const request = tx.objectStore(SUMMARIES).index("ownerId").getAll(ownerId);
      request.onsuccess = () => resolve((request.result as Array<{ summary: Salida }>).map((item) => item.summary));
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

export async function cacheRemoteSummaries(ownerId: string, items: Salida[]): Promise<void> {
  const db = await dbBookmarks();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(SUMMARIES, "readwrite");
      const store = tx.objectStore(SUMMARIES);
      const current = store.index("ownerId").openCursor(IDBKeyRange.only(ownerId));
      const next = new Set(items.map((item) => item.id));
      current.onsuccess = () => {
        const cursor = current.result;
        if (!cursor) {
          for (const summary of items) store.put({ key: `${ownerId}:${summary.id}`, ownerId, summary });
          return;
        }
        if (!next.has((cursor.value as { summary: Salida }).summary.id)) cursor.delete();
        cursor.continue();
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

export async function loadPreview(ownerId: string, activityId: string): Promise<Coordenada[] | null> {
  const db = await dbBookmarks();
  try {
    return await new Promise<Coordenada[] | null>((resolve, reject) => {
      const tx = db.transaction(PREVIEWS, "readonly");
      const request = tx.objectStore(PREVIEWS).get(`${ownerId}:${activityId}`);
      request.onsuccess = () => resolve((request.result as { points?: Coordenada[] } | undefined)?.points ?? null);
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

export async function cachePreview(ownerId: string, activityId: string, points: Coordenada[]): Promise<void> {
  if (points.length < 2 || points.length > 180) return;
  const db = await dbBookmarks();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(PREVIEWS, "readwrite");
      tx.objectStore(PREVIEWS).put({ key: `${ownerId}:${activityId}`, ownerId, points });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

export async function listLocalBookmarks(ownerId: string): Promise<Bookmark[]> {
  const db = await dbBookmarks();
  try {
    return await new Promise<Bookmark[]>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const request = tx.objectStore(STORE).index("ownerId").getAll(ownerId);
      request.onsuccess = () => resolve((request.result as Bookmark[]).map((item) => normalizeBookmark(item)));
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

export async function setLocalBookmark(ownerId: string, activityId: string, saved: boolean): Promise<void> {
  if (!ownerId || !activityId) throw new Error("Necesitas una sesion y una salida terminada.");
  const db = await dbBookmarks();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const key = `${ownerId}:${activityId}`;
      const request = store.get(key);
      request.onsuccess = () => {
        const current = request.result ? normalizeBookmark(request.result as Bookmark) : null;
        store.put({
          key,
          ownerId,
          activityId,
          saved,
          updatedAt: Date.now(),
          syncStatus: "pending",
          serverVersion: current?.serverVersion ?? null,
        } satisfies Bookmark);
      };
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

/**
 * Remote state replaces only already-synced local state.
 * A pending offline choice always survives a refresh until it is applied or
 * explicitly resolved as a server-version conflict.
 */
export async function cacheRemoteBookmarks(ownerId: string, remote: ReadonlyMap<string, RemoteBookmarkState>): Promise<void> {
  const existing = await listLocalBookmarks(ownerId);
  const previousById = new Map(existing.map((item) => [item.activityId, item]));
  const db = await dbBookmarks();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      for (const item of existing) {
        if (item.syncStatus === "pending") continue;
        if (!remote.has(item.activityId)) store.delete(item.key);
      }
      for (const state of remote.values()) {
        const prior = previousById.get(state.activityId);
        if (prior?.syncStatus === "pending") continue;
        store.put({
          key: `${ownerId}:${state.activityId}`,
          ownerId,
          activityId: state.activityId,
          saved: state.saved,
          updatedAt: state.updatedAt,
          syncStatus: "synced",
          serverVersion: state.serverVersion,
        } satisfies Bookmark);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

/**
 * Finishes one local mutation only if it is still the same mutation that was
 * sent. This prevents a late response from erasing a newer offline choice.
 */
export async function resolveBookmarkMutation(
  ownerId: string,
  activityId: string,
  localUpdatedAt: number,
  remote: RemoteBookmarkState | null,
): Promise<void> {
  const db = await dbBookmarks();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const key = `${ownerId}:${activityId}`;
      const request = store.get(key);
      request.onsuccess = () => {
        const raw = request.result as Bookmark | undefined;
        if (!raw) return;
        const current = normalizeBookmark(raw);
        if (current.updatedAt !== localUpdatedAt) return;
        if (!remote) {
          store.delete(key);
          return;
        }
        store.put({
          key,
          ownerId,
          activityId,
          saved: remote.saved,
          updatedAt: remote.updatedAt,
          syncStatus: "synced",
          serverVersion: remote.serverVersion,
        } satisfies Bookmark);
      };
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

export function savedIdsFromLocal(bookmarks: Bookmark[]): Set<string> {
  return new Set(bookmarks.filter((item) => item.saved).map((item) => item.activityId));
}

function toSummary(session: NavigationSession): Salida {
  return {
    id: session.id,
    ownerId: session.ownerId,
    routeId: session.routeId,
    name: session.routeName || "Mi recorrido",
    kind: session.kind,
    startedAt: session.startedAt,
    finishedAt: session.finishedAt ?? 0,
    distanceM: Math.round((session.stats?.recordedDistanceKm ?? 0) * 1000),
    durationS: session.elapsedSeconds,
    ascentM: session.stats?.ascentM ?? 0,
    pointsCount: session.points?.length ?? 0,
    syncStatus: session.syncStatus,
    source: "local",
  };
}

/**
 * Reads the full local owner index. This is display/input data only.
 * Mi mapa V2 NEVER synchronizes activities and NEVER replaces THERA History / Sync Scale V4.
 */
export async function listLocalActivitySummaries(ownerId: string): Promise<Salida[]> {
  if (!("indexedDB" in globalThis)) return [];
  const db = await openDeviceDatabase();
  try {
    return await new Promise<Salida[]>((resolve, reject) => {
      const output: Salida[] = [];
      const tx = db.transaction(ACTIVITY_HISTORY_STORE, "readonly");
      const request = tx.objectStore(ACTIVITY_HISTORY_STORE).index("ownerId").openCursor(IDBKeyRange.only(ownerId));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        const session = cursor.value as NavigationSession;
        if (session.state === "finished" && session.finishedAt) output.push(toSummary(session));
        cursor.continue();
      };
      tx.oncomplete = () => resolve(output);
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

export async function getLocalActivity(ownerId: string, id: string): Promise<NavigationSession | null> {
  if (!("indexedDB" in globalThis)) return null;
  const db = await openDeviceDatabase();
  try {
    const session = await new Promise<NavigationSession | undefined>((resolve, reject) => {
      const tx = db.transaction(ACTIVITY_HISTORY_STORE, "readonly");
      const request = tx.objectStore(ACTIVITY_HISTORY_STORE).get(id);
      request.onsuccess = () => resolve(request.result as NavigationSession | undefined);
      request.onerror = () => reject(request.error);
    });
    return session?.ownerId === ownerId && session.state === "finished" ? session : null;
  } finally { db.close(); }
}

export async function localTrack(ownerId: string, id: string): Promise<unknown> {
  const session = await getLocalActivity(ownerId, id);
  return session ? activityGeoJson(session.points, session.markers) : null;
}
